/**
 * MergePersonsModal - Merge multiple persons with undo support
 *
 * Features:
 * - Select primary person (target of merge)
 * - Preview merged face count
 * - 24h undo period (soft delete archived records)
 * - Warning about irreversibility after 24h
 */

import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { mergePersons } from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import styles from './MergePersonsModal.module.css';

export default function MergePersonsModal({ persons, onClose, onSuccess }) {
  const [primaryId, setPrimaryId] = useState(persons[0]?.id);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  // Calculate totals
  const stats = useMemo(() => {
    const totalFaces = persons.reduce((sum, p) => sum + (p.face_count || 0), 0);
    const primary = persons.find((p) => p.id === primaryId);
    const toMerge = persons.filter((p) => p.id !== primaryId);
    return { totalFaces, primary, toMerge };
  }, [persons, primaryId]);

  const handleMerge = async () => {
    if (!primaryId || stats.toMerge.length === 0) return;

    setMerging(true);
    setError(null);

    try {
      await mergePersons({
        primary_id: primaryId,
        merge_ids: stats.toMerge.map((p) => p.id),
      });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        Merge Persons
      </Modal.Header>
      <Modal.Body>
        <p className={styles.description}>
          Merge {persons.length} persons into one. Select the primary person that will
          remain after the merge. All faces from merged persons will be reassigned.
        </p>

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Select Primary Person</h4>
          <div className={styles.personList}>
            {persons.map((person) => (
              <label
                key={person.id}
                className={clsx(
                  styles.personOption,
                  primaryId === person.id && styles.selected
                )}
              >
                <input
                  type="radio"
                  name="primary"
                  value={person.id}
                  checked={primaryId === person.id}
                  onChange={() => setPrimaryId(person.id)}
                  className={styles.radio}
                />
                <div className={styles.personInfo}>
                  <span className={styles.personName}>{person.name}</span>
                  {person.global_name && person.global_name !== person.name && (
                    <span className={styles.globalName}>({person.global_name})</span>
                  )}
                </div>
                <div className={styles.personMeta}>
                  <span className={styles.role}>{person.role || 'player'}</span>
                  <span className={styles.faceCount}>{person.face_count || 0} faces</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Merge Preview</h4>
          <div className={styles.preview}>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>Primary Person:</span>
              <span className={styles.previewValue}>{stats.primary?.name}</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>Persons to Merge:</span>
              <span className={styles.previewValue}>
                {stats.toMerge.map((p) => p.name).join(', ')}
              </span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>Total Faces After Merge:</span>
              <span className={styles.previewValue}>{stats.totalFaces}</span>
            </div>
          </div>
        </div>

        <div className={styles.warning}>
          <div className={styles.warningIcon}>!</div>
          <div className={styles.warningContent}>
            <strong>24-Hour Undo Period</strong>
            <p>
              Merged persons will be archived for 24 hours. During this time, you can
              undo the merge from the Archived Persons section. After 24 hours, the
              merge becomes permanent.
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={merging}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleMerge}
          disabled={merging || stats.toMerge.length === 0}
        >
          {merging ? 'Merging...' : `Merge ${stats.toMerge.length} Persons`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
